import { createContextFactory } from '@mfw/components';
import { useFormContext } from 'react-hook-form';
import React from 'react';

import type { UiAddressCountry } from '../../schemas/address.schema';
import type { Maybe } from '../../types/maybe.types';

import { useInfiniteCountries } from '../../hooks';

export type AddressContextType = {
  setDefaultCountry: React.Dispatch<
    React.SetStateAction<Maybe<UiAddressCountry>>
  >;
  fetchNextPage: ReturnType<typeof useInfiniteCountries>['fetchNextPage'];
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  setPending: React.Dispatch<React.SetStateAction<boolean>>;
  defaultCountry: Maybe<UiAddressCountry>;
  countries: Maybe<UiAddressCountry[]>;
  isFetchingCountries: boolean;
  totalCountries: number;
  isPending: boolean;
};

export const AddressContext = createContextFactory({
  devToolsName: 'AddressContext',
  initialValue: {} as AddressContextType,
});

export interface AddressProviderProps {
  children: React.ReactNode;
}

export const AddressProvider = ({ children }: AddressProviderProps) => {
  const [isPending, setPending] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [defaultCountry, setDefaultCountry] =
    React.useState<Maybe<UiAddressCountry>>(undefined);

  const useInfiniteCountriesArgs = React.useMemo(() => {
    return {
      limit: '25',
      search: searchTerm,
    };
  }, [searchTerm]);

  const {
    data: countries,
    fetchNextPage,
    isPending: isFetchingCountries,
  } = useInfiniteCountries(useInfiniteCountriesArgs);

  const totalCountries = countries?.total || 0;
  const providedValue = React.useMemo(
    () => ({
      countries: countries?.data,
      defaultCountry,
      fetchNextPage,
      isFetchingCountries,
      isPending: isFetchingCountries || isPending,
      searchTerm,
      setDefaultCountry,
      setPending,
      setSearchTerm,
      totalCountries,
    }),
    [
      searchTerm,
      isFetchingCountries,
      defaultCountry,
      fetchNextPage,
      setPending,
      totalCountries,
      setDefaultCountry,
      countries?.data,
      isPending,
    ],
  );
  return (
    <AddressContext.Provider value={providedValue}>
      {children}
    </AddressContext.Provider>
  );
};

export interface AddressControllerProps {
  /**
   * This should be name of country field in form. Let's say you whole address component has name "address",
   * and your country field has "address.country", then you should pass "address.country" here.
   */
  name: string;
}
/**
 * Component should be used as a child of a FormProvider.
 * This will pickup value of form for specific country field and make sure that country is fetched correctly.
 */

export const AddressController = ({ name }: AddressControllerProps) => {
  const { setDefaultCountry, setPending } = useAddressContext();
  const { watch } = useFormContext();
  const value = watch(name) as Array<{ value?: string }> | undefined;

  const defaultCountryArgs = React.useMemo(() => {
    return {
      limit: '25',
      search: value?.[0]?.value ?? '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.[0]?.value]);

  const { data: foundCountries, isPending: isPendingSingleCountry } =
    useInfiniteCountries(defaultCountryArgs);

  React.useEffect(() => {
    const found = foundCountries?.data?.find(
      (country) => country.value === value?.[0]?.value,
    );
    if (found) {
      setDefaultCountry(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foundCountries, setDefaultCountry, value?.[0]?.value]);

  React.useEffect(() => {
    setPending(isPendingSingleCountry);
  }, [isPendingSingleCountry, setPending]);

  return null;
};

export function useAddressContext() {
  return AddressContext.useContext();
}
